<template>
  <block-item-card
    :min-height="minBlockHeight"
    :title="$t('RiskLiquidity.Section.BankFailures.Blocks.ListOfUSBankFailures.Title')"
  >
    <v-row
      class="ma-0 px-2"
      no-gutters
    >
      <v-col
        class="ma-0 pa-2"
        cols="12"
      >
        <!-- Filters Row -->
        <v-row
          class="mb-4"
          no-gutters
        >
          <!-- Left side: City/State and Year filters -->
          <v-col
            cols="12"
            md="5"
            class="pa-0"
          >
            <v-row no-gutters>
              <!-- Select State -->
              <v-col
                cols="12"
                md="4"
                class="pa-0 pr-md-2 pt-2 pt-md-0"
              >
                <div class="text-size-14 font-weight-medium mb-1 synthesis-brand-dark-blue--text">
                  {{ $t('RiskLiquidity.Section.BankFailures.Blocks.ListOfUSBankFailures.Filters.SelectState') }}
                </div>
                <v-autocomplete
                  v-model="filters.selectedState"
                  :items="availableStateOptions"
                  :placeholder="$t('RiskLiquidity.Section.BankFailures.Blocks.ListOfUSBankFailures.Filters.All')"
                  outlined
                  dense
                  clearable
                  hide-details
                />
              </v-col>

              <!-- Select City -->
              <v-col
                cols="12"
                md="4"
                class="pa-0 pr-md-2 pt-2 pt-md-0"
              >
                <div class="text-size-14 font-weight-medium mb-1 synthesis-brand-dark-blue--text">
                  {{ $t('RiskLiquidity.Section.BankFailures.Blocks.ListOfUSBankFailures.Filters.SelectCity') }}
                </div>
                <v-autocomplete
                  v-model="filters.selectedCity"
                  :items="availableCityOptions"
                  :placeholder="$t('RiskLiquidity.Section.BankFailures.Blocks.ListOfUSBankFailures.Filters.All')"
                  outlined
                  dense
                  clearable
                  hide-details
                />
              </v-col>

              <!-- Select Year -->
              <v-col
                cols="12"
                md="4"
                class="pa-0 pl-md-2 pt-2 pt-md-0"
              >
                <div class="text-size-14 font-weight-medium mb-1 synthesis-brand-dark-blue--text">
                  {{ $t('RiskLiquidity.Section.BankFailures.Blocks.ListOfUSBankFailures.Filters.SelectYear') }}
                </div>
                <v-select
                  v-model="filters.selectedYear"
                  :items="availableYearOptions"
                  :placeholder="$t('RiskLiquidity.Section.BankFailures.Blocks.ListOfUSBankFailures.Filters.All')"
                  outlined
                  dense
                  clearable
                  hide-details
                />
              </v-col>
            </v-row>
          </v-col>

          <!-- Right side: Sorting filter -->
          <v-col
            cols="12"
            md="7"
            class="pa-0 pl-md-8"
          >
            <v-row
              no-gutters
              justify="end"
              class="pt-2 pt-md-0"
            >
              <!-- Sorted by -->
              <v-col
                cols="12"
                md="3"
                class="pa-0"
              >
                <div class="text-size-14 font-weight-medium mb-1 synthesis-brand-dark-blue--text">
                  {{ $t('RiskLiquidity.Section.BankFailures.Blocks.ListOfUSBankFailures.Filters.SortedBy.Title') }}
                </div>
                <v-card
                  class="pa-2 px-4"
                  flat
                  rounded
                  style="border:1px solid rgba(223, 227, 235, 1);"
                >
                  <banking-switch
                    v-model="sortByAssets"
                    :enabled-label="$t('RiskLiquidity.Section.BankFailures.Blocks.ListOfUSBankFailures.Filters.SortedBy.Toggle.ValueOfAssets')"
                    :disabled-label="$t('RiskLiquidity.Section.BankFailures.Blocks.ListOfUSBankFailures.Filters.SortedBy.Toggle.FailureDate')"
                  />
                </v-card>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <!-- Bank Failures Table -->
        <v-row
          v-if="bankFailures && bankFailures.length"
          class="mt-3"
        >
          <v-col>
            <table-header-gradient-blue>
              <template #table>
                <v-data-table
                  :headers="table.headers"
                  :items="bankFailures"
                  :loading="loading"
                  class="font-weight-medium synthesis-card-border"
                  disable-pagination
                  disable-sort
                  hide-default-footer
                >
                  <template #header.Date="{header}">
                    {{ header.text }}
                  </template>

                  <template #header.Bank="{header}">
                    {{ header.text }}
                  </template>

                  <template #header.Established="{header}">
                    {{ header.text }}
                  </template>

                  <template #header.City="{header}">
                    {{ header.text }}
                  </template>

                  <template #header.State="{header}">
                    {{ header.text }}
                  </template>

                  <template #header.Assets="{header}">
                    {{ header.text }}
                  </template>

                  <template #header.Location="{header}">
                    {{ header.text }}
                  </template>

                  <template #item.Bank="{item}">
                    <span class="font-weight-medium">{{ item.bank }}</span>
                  </template>

                  <template #item.Date="{item}">
                    {{ formatDate(item.date) }}
                  </template>

                  <template #item.Established="{item}">
                    {{ item.established }}
                  </template>

                  <template #item.City="{item}">
                    {{ item.city }}
                  </template>

                  <template #item.State="{item}">
                    {{ item.state }}
                  </template>

                  <template #item.Assets="{item}">
                    {{ formatNumberShort(item.assets) }}
                  </template>
                </v-data-table>
              </template>
            </table-header-gradient-blue>
          </v-col>
        </v-row>

        <!-- No Data Message -->
        <v-row
          v-else-if="!loading"
          class="mt-3"
        >
          <v-col class="text-center">
            <v-card
              class="synthesis-inter-text rounded-lg"
              color="synthesis-ui-grey-05"
              flat
              rounded
            >
              <v-card-text class="pa-5 synthesis-ui-grey-01--text">
                {{ $t('RiskLiquidity.Section.BankFailures.NoData') }}
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </block-item-card>
</template>

<script>
import BlockItemCard from '@/theme/default/components/common/BlockItemCard'
import TableHeaderGradientBlue from '@/theme/default/components/common/TableHeaderGradientBlue'
import BankingSwitch from '@/theme/default/components/common/BankingSwitch'
import chartsColorMixin from '@/theme/default/components/charts/mixins/chartsColorMixin'
import Auth from '@/mixins/auth'
import { toNumberFormatNotationShort } from '@/lib/utils/helper'
import API from '@/api/Api'

export default {
  name      : 'ListOfUSBankFailures',
  components: {
    BlockItemCard,
    TableHeaderGradientBlue,
    BankingSwitch
  },
  mixins   : [chartsColorMixin, Auth],
  props    : {},
  dataStore: {
    ListOfUSBankFailures: 'RiskLiquidity.BankFailures.ListOfUSBankFailures.Data',
    CityStateOptions    : 'RiskLiquidity.BankFailures.CityStateOptions.Data',
    YearOptions         : 'RiskLiquidity.BankFailures.YearOptions.Data'
  },
  data () {
    return {
      minBlockHeight     : 350,
      loading            : false,
      filterDebounceTimer: null,
      filters            : {
        selectedYear : null,
        selectedCity : null,
        selectedState: null,
        sortByAssets : false // false = sort by date (default), true = sort by assets
      },
      table: {
        headers: [
          {
            text : this.$t('RiskLiquidity.Section.BankFailures.Blocks.ListOfUSBankFailures.TableHeaders.Date'),
            value: 'Date',
            align: 'start',
            class: 'grey lighten-5'
          },
          {
            text : this.$t('RiskLiquidity.Section.BankFailures.Blocks.ListOfUSBankFailures.TableHeaders.Bank'),
            value: 'Bank',
            class: 'grey lighten-5'
          },
          {
            text : this.$t('RiskLiquidity.Section.BankFailures.Blocks.ListOfUSBankFailures.TableHeaders.Established'),
            value: 'Established',
            class: 'grey lighten-5'
          },
          {
            text : this.$t('RiskLiquidity.Section.BankFailures.Blocks.ListOfUSBankFailures.TableHeaders.City'),
            value: 'City',
            class: 'grey lighten-5'
          },
          {
            text : this.$t('RiskLiquidity.Section.BankFailures.Blocks.ListOfUSBankFailures.TableHeaders.State'),
            value: 'State',
            class: 'grey lighten-5'
          },
          {
            text : this.$t('RiskLiquidity.Section.BankFailures.Blocks.ListOfUSBankFailures.TableHeaders.Assets'),
            value: 'Assets',
            class: 'grey lighten-5'
          }
        ]
      }
    }
  },
  computed: {
    accountId () {
      return this.user?.selectedAccountId || null
    },
    baseCurrency () {
      return this.ListOfUSBankFailures?.Data?.baseCurrency || null
    },
    citiesByStateOptions () {
      return [...(this.CityStateOptions?.Data?.citiesByStateOptions || [])].reduce((acc, curr) => {
        acc[curr.state] = curr.cityOptions
        return acc
      }, {})
    },
    currencySymbol () {
      return this.baseCurrency?.displaySymbol || ''
    },
    availableYearOptions () {
      const options = this.YearOptions?.Data || []
      return options
    },
    availableStateOptions () {
      const options = this.CityStateOptions?.Data?.stateOptions || []
      return options
    },
    availableCityOptions () {
      const allCityOptions = this.CityStateOptions?.Data?.cityOptions || []

      // If a state is selected, filter cities to only show those in that state
      if (this.filters.selectedState) {
        const citiesForState = this.citiesByStateOptions[this.filters.selectedState] || []
        return allCityOptions.filter(city => citiesForState.includes(city))
      }

      // If no state is selected, show all cities
      return allCityOptions
    },
    bankFailures () {
      return this.ListOfUSBankFailures?.Data?.bankFailures || []
    },
    sortByAssets: {
      get () {
        return this.filters.sortByAssets
      },
      set (value) {
        this.filters.sortByAssets = value
      }
    }
  },

  watch: {
    accountId: {
      immediate: true,
      handler (newAccountId) {
        if (newAccountId) {
          this.getBankFailuresYearOptions()
          this.getBankFailuresCityStateOptions()
          this.getListOfUSBankFailures()
        }
      }
    },
    'filters.selectedYear': {
      handler () {
        this.debouncedGetListOfUSBankFailures()
      }
    },
    'filters.selectedState': {
      handler (newState) {
        // If a state is selected, clear the city if it's not in the filtered list
        if (newState && this.filters.selectedCity) {
          const citiesForState = this.citiesByStateOptions[newState] || []
          if (!citiesForState.includes(this.filters.selectedCity)) {
            this.filters.selectedCity = null
          }
        }
        this.debouncedGetListOfUSBankFailures()
      }
    },
    'filters.selectedCity': {
      handler () {
        this.debouncedGetListOfUSBankFailures()
      }
    },
    'filters.sortByAssets': {
      handler () {
        this.debouncedGetListOfUSBankFailures()
      }
    }
  },

  beforeDestroy () {
    if (this.filterDebounceTimer) {
      clearTimeout(this.filterDebounceTimer)
    }
  },
  methods: {
    debouncedGetListOfUSBankFailures () {
      if (this.filterDebounceTimer) {
        clearTimeout(this.filterDebounceTimer)
      }
      this.filterDebounceTimer = setTimeout(() => {
        this.getListOfUSBankFailures()
      }, 300)
    },
    formatNumberShort (value) {
      return this.currencySymbol + toNumberFormatNotationShort(value)
    },
    async getBankFailuresYearOptions () {
      if (!this.accountId) {
        return
      }
      const response = await API.Resource.RiskLiquidity.GetBankFailuresYearOptions(this.accountId)
      this.handleResponse(response, 'bankFailuresYearOptions')
    },
    async getBankFailuresCityStateOptions () {
      if (!this.accountId) {
        return
      }
      const response = await API.Resource.RiskLiquidity.GetBankFailuresCityStateOptions(this.accountId)
      this.handleResponse(response, 'bankFailuresCityStateOptions')
    },
    async getListOfUSBankFailures () {
      if (!this.accountId || this.loading) {
        return
      }
      this.loading = true
      try {
        const {
          selectedYear,
          selectedCity,
          selectedState,
          sortByAssets
        } = this.filters

        // Convert the single toggle to the API format
        const assetsSortOrder = sortByAssets ? 'ASCENDING' : null
        const dateSortOrder = !sortByAssets ? 'ASCENDING' : null

        const response = await API.Resource.RiskLiquidity.ListBankFailures(this.accountId, {
          selectedYear,
          selectedCity,
          selectedState,
          assetsSortOrder,
          dateSortOrder
        })
        this.handleResponse(response, 'listBankFailures')
      } finally {
        this.loading = false
      }
    },
    handleResponse (response, key) {
      const data = API.responseData(response)?.[key] || null
      const errors = API.responseErrors(response) || []

      if (API.isResponseSuccess(response) && data) {
        // Map the response key to the correct dataStore property
        const dataStoreKey = this.getDataStoreKey(key)
        if (dataStoreKey) {
          this.$set(this[dataStoreKey], 'Data', data)
        }
      } else {
        this.$bus.$emit('app:error', errors)
      }
    },
    getDataStoreKey (responseKey) {
      // Map response keys to dataStore property names
      const keyMap = {
        bankFailuresYearOptions     : 'YearOptions',
        bankFailuresCityStateOptions: 'CityStateOptions',
        listBankFailures            : 'ListOfUSBankFailures'
      }
      return keyMap[responseKey]
    },
    formatDate (dateString) {
      return new Date(dateString).toLocaleDateString('en-US', {
        year : '2-digit',
        month: '2-digit',
        day  : '2-digit'
      })
    }
  }
}
</script>

<style scoped>
</style>
